import React, { useEffect, useState } from 'react';
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, InputAdornment, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import EntityViewDrawer, { EntitySection } from './ui/EntityViewDrawer';
import ConfirmDialog from './ui/ConfirmDialog';
import { useToast } from './ui/ToastProvider';
import { VALID_MTOS, type ValidMTO, VALID_SUB_AGENTS } from '../utils/constants';
// Integration modes
const INTEGRATION_MODES = [{
  value: 'push',
  label: 'Push',
  color: '#3B82F6'
}, {
  value: 'pull',
  label: 'Pull',
  color: '#8B5CF6'
}, {
  value: 'file',
  label: 'File',
  color: '#F59E0B'
}];
// Transaction types
const TRANSACTION_TYPES = [{
  value: 'otc',
  label: 'OTC',
  tooltip: 'Over The Counter',
  color: '#8B5CF6'
}, {
  value: 'ft',
  label: 'FT',
  tooltip: 'Funds Transfer',
  color: '#10B981'
}, {
  value: 'ibft',
  label: 'IBFT',
  tooltip: 'Interbank Funds Transfer',
  color: '#F59E0B'
}];
interface Partner {
  id: string;
  partnerName: string;
  country: string;
  address: string;
  pointOfContact: string;
  contactNumber: string;
  settlementAccount: string;
  status: 'active' | 'inactive';
  integrationMode: string;
  transactionsAllowed: string[];
  configuredSubagents: string[];
  email?: string;
  createdAt: string;
  updatedAt: string;
}
interface PartnersTableProps {
  onEditPartner: (partner: Partner) => void;
  onManageUsers: (partnerId: string) => void;
  searchQuery: string;
}
const COUNTRIES = ['Ghana', 'Nigeria', 'Senegal', 'Uganda', 'Kenya', "Côte d'Ivoire", 'Togo', 'Cameroon', 'Benin'];
// Column label mapping
const COLUMN_LABELS: {
  [key: string]: string;
} = {
  partnerName: 'Partner Name',
  integrationMode: 'Integration Mode',
  transactionsAllowed: 'Transactions Allowed',
  country: 'Country',
  pointOfContact: 'Point of Contact',
  contactNumber: 'Contact Number',
  settlementAccount: 'Settlement Account',
  status: 'Status',
  transactionType: 'Transaction Type'
};
// Mock data
const MOCK_PARTNERS: Partner[] = Array(20).fill(null).map((_, index) => ({
  id: `PTN-${1000 + index}`,
  partnerName: VALID_MTOS[index % VALID_MTOS.length],
  country: COUNTRIES[index % COUNTRIES.length],
  address: `Building ${index + 1}, Independence Ave, Accra, Ghana`,
  pointOfContact: ['Kwame Boateng', 'Ngozi Okonkwo', 'Fatou Diop', 'Joseph Mukasa', 'Wanjiku Kamau'][index % 5],
  contactNumber: [`+233 20 ${1000000 + index}`, `+234 81 ${1000000 + index}`, `+221 77 ${1000000 + index}`, `+256 70 ${1000000 + index}`, `+254 722 ${100000 + index}`][index % 5],
  settlementAccount: ['1110-GH-USD', '1110-NG-USD', 'VA-PAGA-GH-USD', 'VA-FLUTTER-NG', 'VA-MG-GH-USD'][index % 5],
  status: index % 7 === 0 ? 'inactive' : 'active',
  integrationMode: ['push', 'pull', 'file'][index % 3],
  transactionsAllowed: (() => {
    const types = ['otc', 'ft', 'ibft'];
    if (index % 4 === 0) return ['otc'];
    if (index % 4 === 1) return ['ft', 'ibft'];
    if (index % 4 === 2) return ['otc', 'ft'];
    return types;
  })(),
  configuredSubagents: (() => {
    if (index % 3 === 0) return ['Ecobank Ghana Agency'];
    if (index % 3 === 1) return ['Ecobank Nigeria Agency'];
    return ['Ecobank Ghana Agency', 'Ecobank Nigeria Agency'];
  })(),
  email: ['partners@gtbank.com.gh', 'partnerships@firstbanknigeria.com', 'business@paga.com', 'partnersupport@airtel.ug', 'international@kcbgroup.com'][index % 5],
  createdAt: new Date(Date.now() - index * 24 * 60 * 60 * 1000).toISOString(),
  updatedAt: new Date(Date.now() - index * 12 * 60 * 60 * 1000).toISOString()
}));
const PartnersTable: React.FC<PartnersTableProps> = ({
  onEditPartner,
  onManageUsers,
  searchQuery
}) => {
  const [filters, setFilters] = useState({
    countries: [] as string[],
    status: 'all',
    search: searchQuery || '',
    integrationMode: [] as string[],
    transactionTypes: [] as string[],
    partnerName: '',
    pointOfContact: '',
    contactNumber: '',
    settlementAccount: ''
  });
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string | string[];
  }>({});
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [isLoadingPartner, setIsLoadingPartner] = useState(false);
  const [partnerError, setPartnerError] = useState<string | undefined>();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | undefined>();
  const {
    showToast
  } = useToast();
  // Update search when searchQuery prop changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: searchQuery || ''
    }));
  }, [searchQuery]);
  // Handle column filter change
  const handleColumnFilterChange = (columnId: string, value: string | string[]) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: value
    }));
  };
  // Helper function to render transaction type chips
  const renderTransactionChips = (types: string[]) => <Box sx={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5
  }} data-id="element-1266">
      {types.map(type => {
      const transactionType = TRANSACTION_TYPES.find(t => t.value === type);
      return <Tooltip key={type} title={transactionType?.tooltip || ''} data-id="element-1267">
            <Chip label={transactionType?.label || type} size="small" sx={{
          backgroundColor: `${transactionType?.color}15` || 'rgba(14, 165, 233, 0.1)',
          color: transactionType?.color || '#0EA5E9',
          height: 24,
          fontWeight: 600,
          fontSize: '0.7rem'
        }} data-id="element-1268" />
          </Tooltip>;
    })}
    </Box>;
  // Helper function to render subagent chips
  const renderSubagentChips = (subagents: string[]) => <Box sx={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5
  }} data-id="element-1269">
      {subagents.map(subagent => <Chip key={subagent} label={subagent} size="small" sx={{
      backgroundColor: '#F1F5F9',
      color: '#334155',
      height: 24,
      fontWeight: 500,
      fontSize: '0.7rem'
    }} data-id="element-1270" />)}
    </Box>;
  // Define columns
  const columns: Column<Partner>[] = [{
    id: 'partnerName',
    label: 'Partner Name',
    renderFilter: () => <TextField size="small" placeholder="Search name" value={filters.partnerName} onChange={e => handleTextFilterChange('partnerName', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1272">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1273" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1271" />
  }, {
    id: 'integrationMode',
    label: 'Integration Mode',
    renderCell: row => {
      const integrationMode = INTEGRATION_MODES.find(m => m.value === row.integrationMode);
      return <Chip label={integrationMode?.label || row.integrationMode} size="small" sx={{
        backgroundColor: `${integrationMode?.color}15` || 'rgba(14, 165, 233, 0.1)',
        color: integrationMode?.color || '#0EA5E9',
        height: 24,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-1274" />;
    },
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1275">
          <Select value={columnFilters.integrationMode || 'all'} onChange={e => handleColumnFilterChange('integrationMode', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1276">
            <MenuItem value="all" data-id="element-1277">All Modes</MenuItem>
            {INTEGRATION_MODES.map(mode => <MenuItem key={mode.value} value={mode.value} data-id="element-1278">
                {mode.label}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'transactionsAllowed',
    label: 'Transactions Allowed',
    renderCell: row => renderTransactionChips(row.transactionsAllowed),
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1279">
          <Select value={columnFilters.transactionType || 'all'} onChange={e => handleColumnFilterChange('transactionType', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1280">
            <MenuItem value="all" data-id="element-1281">All Types</MenuItem>
            {TRANSACTION_TYPES.map(type => <MenuItem key={type.value} value={type.value} data-id="element-1282">
                {type.label}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'configuredSubagents',
    label: 'Configured Subagents',
    renderCell: row => renderSubagentChips(row.configuredSubagents)
  }, {
    id: 'country',
    label: 'Country',
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1283">
          <Select value={columnFilters.country || 'all'} onChange={e => handleColumnFilterChange('country', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1284">
            <MenuItem value="all" data-id="element-1285">All Countries</MenuItem>
            {COUNTRIES.map(country => <MenuItem key={country} value={country} data-id="element-1286">
                {country}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'pointOfContact',
    label: 'Point of Contact',
    renderFilter: () => <TextField size="small" placeholder="Search contact" value={filters.pointOfContact} onChange={e => handleTextFilterChange('pointOfContact', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1288">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1289" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1287" />
  }, {
    id: 'contactNumber',
    label: 'Contact Number',
    renderFilter: () => <TextField size="small" placeholder="Search number" value={filters.contactNumber} onChange={e => handleTextFilterChange('contactNumber', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1291">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1292" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1290" />
  }, {
    id: 'settlementAccount',
    label: 'Settlement Account',
    renderFilter: () => <TextField size="small" placeholder="Search account" value={filters.settlementAccount} onChange={e => handleTextFilterChange('settlementAccount', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1294">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1295" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1293" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24,
      fontWeight: 600
    }} data-id="element-1296" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1297">
          <Select value={columnFilters.status || 'all'} onChange={e => handleColumnFilterChange('status', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1298">
            <MenuItem value="all" data-id="element-1299">All Status</MenuItem>
            <MenuItem value="active" data-id="element-1300">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-1301">Inactive</MenuItem>
          </Select>
        </FormControl>
  }];
  // Apply filters to the data
  const filteredPartners = MOCK_PARTNERS.filter(partner => {
    // Apply search query
    if (filters.search) {
      const query = filters.search.toLowerCase();
      if (!partner.partnerName.toLowerCase().includes(query) && !partner.country.toLowerCase().includes(query) && !partner.pointOfContact.toLowerCase().includes(query) && !partner.contactNumber.toLowerCase().includes(query) && !partner.email?.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Column-specific filters
    if (filters.partnerName && !partner.partnerName.toLowerCase().includes(filters.partnerName.toLowerCase())) {
      return false;
    }
    if (filters.pointOfContact && !partner.pointOfContact.toLowerCase().includes(filters.pointOfContact.toLowerCase())) {
      return false;
    }
    if (filters.contactNumber && !partner.contactNumber.toLowerCase().includes(filters.contactNumber.toLowerCase())) {
      return false;
    }
    if (filters.settlementAccount && !partner.settlementAccount.toLowerCase().includes(filters.settlementAccount.toLowerCase())) {
      return false;
    }
    // Country filter
    if (filters.countries.length > 0 && !filters.countries.includes(partner.country)) {
      return false;
    }
    // Status filter
    if (filters.status !== 'all' && partner.status !== filters.status) {
      return false;
    }
    // Integration mode filter
    if (filters.integrationMode.length > 0 && !filters.integrationMode.includes(partner.integrationMode)) {
      return false;
    }
    // Transaction types filter
    if (filters.transactionTypes.length > 0) {
      const hasMatch = filters.transactionTypes.some(type => partner.transactionsAllowed.includes(type));
      if (!hasMatch) {
        return false;
      }
    }
    // Column-specific integration mode filter
    if (columnFilters.integrationMode && columnFilters.integrationMode !== 'all' && partner.integrationMode !== columnFilters.integrationMode) {
      return false;
    }
    // Column-specific status filter
    if (columnFilters.status && columnFilters.status !== 'all' && partner.status !== columnFilters.status) {
      return false;
    }
    // Column-specific country filter
    if (columnFilters.country && columnFilters.country !== 'all' && partner.country !== columnFilters.country) {
      return false;
    }
    return true;
  });
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      countries: [],
      status: 'all',
      search: '',
      integrationMode: [],
      transactionTypes: [],
      partnerName: '',
      pointOfContact: '',
      contactNumber: '',
      settlementAccount: ''
    });
    setColumnFilters({});
  };
  // Handle search change
  const handleSearchChange = (value: string) => {
    setFilters({
      ...filters,
      search: value
    });
  };
  // Handle column text filter change
  const handleTextFilterChange = (field: string, value: string) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };
  // Define filter chips (now columns is already defined)
  const filterChips: FilterChip[] = [...(filters.partnerName ? [{
    id: 'partnerName',
    label: 'Partner Name',
    value: filters.partnerName,
    onDelete: () => handleTextFilterChange('partnerName', '')
  }] : []), ...(filters.pointOfContact ? [{
    id: 'pointOfContact',
    label: 'Point of Contact',
    value: filters.pointOfContact,
    onDelete: () => handleTextFilterChange('pointOfContact', '')
  }] : []), ...(filters.contactNumber ? [{
    id: 'contactNumber',
    label: 'Contact Number',
    value: filters.contactNumber,
    onDelete: () => handleTextFilterChange('contactNumber', '')
  }] : []), ...(filters.settlementAccount ? [{
    id: 'settlementAccount',
    label: 'Settlement Account',
    value: filters.settlementAccount,
    onDelete: () => handleTextFilterChange('settlementAccount', '')
  }] : []), ...filters.countries.map(country => ({
    id: `country-${country}`,
    label: 'Country',
    value: country,
    onDelete: () => setFilters({
      ...filters,
      countries: filters.countries.filter(c => c !== country)
    })
  })), ...(filters.status !== 'all' ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: 'all'
    })
  }] : []), ...filters.integrationMode.map(mode => ({
    id: `mode-${mode}`,
    label: 'Integration Mode',
    value: INTEGRATION_MODES.find(m => m.value === mode)?.label || mode,
    onDelete: () => setFilters({
      ...filters,
      integrationMode: filters.integrationMode.filter(m => m !== mode)
    })
  })), ...filters.transactionTypes.map(type => ({
    id: `type-${type}`,
    label: 'Transaction Type',
    value: TRANSACTION_TYPES.find(t => t.value === type)?.label || type,
    onDelete: () => setFilters({
      ...filters,
      transactionTypes: filters.transactionTypes.filter(t => t !== type)
    })
  })), ...Object.entries(columnFilters).map(([key, value]) => ({
    id: `column-${key}-${value}`,
    label: COLUMN_LABELS[key] || key,
    value: typeof value === 'string' ? value : value.join(', '),
    onDelete: () => handleColumnFilterChange(key, key === 'status' ? 'all' : '')
  }))];
  // Handle view partner
  const handleViewPartner = async (partner: Partner) => {
    setSelectedPartner(partner);
    setViewDrawerOpen(true);
    setIsLoadingPartner(true);
    setPartnerError(undefined);
    try {
      // Simulate API call to fetch partner details
      await new Promise(resolve => setTimeout(resolve, 1000));
      // In a real app, you would fetch detailed partner data here
      setIsLoadingPartner(false);
      showToast(`Partner details loaded`, 'success');
    } catch (error) {
      setPartnerError('Failed to load partner details. Please try again.');
      setIsLoadingPartner(false);
    }
  };
  // Handle status change
  const handleStatusChange = (partner: Partner) => {
    setSelectedPartner(partner);
    setStatusDialogOpen(true);
    setStatusError(undefined);
  };
  // Confirm status change
  const confirmStatusChange = async (reason?: string) => {
    if (!selectedPartner) return;
    setIsChangingStatus(true);
    setStatusError(undefined);
    try {
      // Simulate API call to change status
      await new Promise(resolve => setTimeout(resolve, 1500));
      // In a real app, you would call the API to update status
      // Update local state to reflect the change
      const newStatus = selectedPartner.status === 'active' ? 'inactive' : 'active';
      setSelectedPartner({
        ...selectedPartner,
        status: newStatus
      });
      // Close dialog and show toast
      setStatusDialogOpen(false);
      setIsChangingStatus(false);
      showToast(`Partner ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`, 'success');
      // In a real app, you would refetch the data or update it in state
    } catch (error) {
      setStatusError('Failed to update status. Please try again.');
      setIsChangingStatus(false);
    }
  };
  // Handle manage users (already implemented)
  const handleManageUsers = (partner: Partner) => {
    onManageUsers(partner.id);
    showToast(`Navigated to users for ${partner.partnerName}`, 'info');
  };
  // Prepare sections for view drawer
  const getPartnerSections = (): EntitySection[] => {
    if (!selectedPartner) return [];
    return [{
      title: 'Overview',
      fields: [{
        label: 'Partner ID',
        value: selectedPartner.id
      }, {
        label: 'Status',
        value: selectedPartner.status === 'active' ? 'Active' : 'Inactive',
        type: 'chip',
        chipColor: selectedPartner.status === 'active' ? '#10B981' : '#EF4444',
        chipBgColor: selectedPartner.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'
      }, {
        label: 'Country',
        value: selectedPartner.country
      }, {
        label: 'Integration Mode',
        value: INTEGRATION_MODES.find(m => m.value === selectedPartner.integrationMode)?.label || selectedPartner.integrationMode,
        type: 'chip',
        chipColor: INTEGRATION_MODES.find(m => m.value === selectedPartner.integrationMode)?.color,
        chipBgColor: `${INTEGRATION_MODES.find(m => m.value === selectedPartner.integrationMode)?.color}15`
      }, {
        label: 'Transactions Allowed',
        value: <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5
        }} data-id="element-1302">
                {selectedPartner.transactionsAllowed.map(type => {
            const transactionType = TRANSACTION_TYPES.find(t => t.value === type);
            return <Tooltip key={type} title={transactionType?.tooltip || ''} data-id="element-1303">
                      <Chip label={transactionType?.label || type} size="small" sx={{
                backgroundColor: `${transactionType?.color}15` || 'rgba(14, 165, 233, 0.1)',
                color: transactionType?.color || '#0EA5E9',
                height: 24,
                fontWeight: 600,
                fontSize: '0.7rem'
              }} data-id="element-1304" />
                    </Tooltip>;
          })}
              </Box>
      }, {
        label: 'Configured Subagents',
        value: <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5
        }} data-id="element-1305">
                {selectedPartner.configuredSubagents.map(subagent => <Chip key={subagent} label={subagent} size="small" sx={{
            backgroundColor: '#F1F5F9',
            color: '#334155',
            height: 24,
            fontWeight: 500,
            fontSize: '0.7rem'
          }} data-id="element-1306" />)}
              </Box>
      }]
    }, {
      title: 'Contact Information',
      fields: [{
        label: 'Point of Contact',
        value: selectedPartner.pointOfContact
      }, {
        label: 'Contact Number',
        value: selectedPartner.contactNumber,
        type: 'phone'
      }, {
        label: 'Email',
        value: selectedPartner.email || 'N/A',
        type: 'email'
      }, {
        label: 'Address',
        value: selectedPartner.address
      }]
    }, {
      title: 'Account Details',
      fields: [{
        label: 'Settlement Account',
        value: selectedPartner.settlementAccount
      }, {
        label: 'Created At',
        value: selectedPartner.createdAt,
        type: 'date'
      }, {
        label: 'Last Updated',
        value: selectedPartner.updatedAt,
        type: 'date'
      }]
    }];
  };
  // Define row actions
  const rowActions: RowAction<Partner>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: handleViewPartner
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditPartner
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: handleStatusChange,
    condition: partner => partner.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: handleStatusChange,
    condition: partner => partner.status === 'inactive'
  }, {
    icon: 'lucide:users',
    label: 'Manage Users',
    onClick: handleManageUsers
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: partner => {
      console.log('View audit trail for partner', partner);
      showToast('Audit log feature coming soon', 'info');
    }
  }];
  // Handle export
  const handleExport = () => {
    console.log('Export data');
  };
  return <>
      <DataTable title="Partners" data={filteredPartners} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={handleClearFilters} loading={loading} initialVisibleColumns={['partnerName', 'integrationMode', 'transactionsAllowed', 'configuredSubagents', 'country', 'pointOfContact', 'status']} onSearchChange={handleSearchChange} searchPlaceholder="Search partners by name, country, contact person..." searchValue={filters.search} onExport={handleExport} emptyStateMessage="No partners found" data-id="element-1307" />
      {/* View Drawer */}
      {selectedPartner && <EntityViewDrawer open={viewDrawerOpen} onClose={() => setViewDrawerOpen(false)} onEdit={() => {
      setViewDrawerOpen(false);
      onEditPartner(selectedPartner);
    }} title={selectedPartner.partnerName} subtitle="Partner Details" entityId={selectedPartner.id} sections={getPartnerSections()} isLoading={isLoadingPartner} error={partnerError} onRetry={() => handleViewPartner(selectedPartner)} data-id="element-1308" />}
      {/* Status Change Dialog */}
      {selectedPartner && <ConfirmDialog open={statusDialogOpen} title={`${selectedPartner.status === 'active' ? 'Deactivate' : 'Activate'} Partner`} message={`Are you sure you want to ${selectedPartner.status === 'active' ? 'deactivate' : 'activate'} ${selectedPartner.partnerName}?`} confirmLabel={selectedPartner.status === 'active' ? 'Deactivate' : 'Activate'} showReason={true} reasonLabel="Reason (optional)" reasonPlaceholder={`Enter reason for ${selectedPartner.status === 'active' ? 'deactivation' : 'activation'}...`} onConfirm={confirmStatusChange} onCancel={() => setStatusDialogOpen(false)} isLoading={isChangingStatus} error={statusError} data-id="element-1309" />}
    </>;
};
export default PartnersTable;